import { Link } from "react-router-dom";
import { ArrowRight, Clock, User } from "lucide-react";
import SEO from "../components/SEO";
import { BLOGS } from "../data/blogs";
import useReveal from "../hooks/useReveal";

export default function Blog() {
  useReveal();
  const featured = BLOGS[0];
  const rest = BLOGS.slice(1);

  return (
    <>
      <SEO
        title="Dental Blog - Tips, Costs & Treatment Guides | Lotus Family Dental Allahabad"
        description="Expert dental advice from Allahabad's top dentists. Read guides on root canal cost, dental implants, braces age, tooth decay prevention and more."
        keywords="dental blog allahabad, dentist tips, oral health, dental cost guide"
        canonical="https://lotusfamilydental.me/blog"
      />

      <section className="pt-12 lg:pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold mb-3">
              Insights & Stories
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-900">
              The Lotus <span className="text-teal-700 font-medium">Dental Journal.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600">
              Expert tips, treatment guides, and cost breakdowns from our specialists in Prayagraj.
            </p>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={`/blog/${featured.slug}`}
            className="reveal block group bg-white border border-slate-100 rounded-3xl overflow-hidden premium-card"
            data-testid={`blog-featured-${featured.slug}`}
          >
            <div className="grid lg:grid-cols-2 gap-0">
              <div className="aspect-[16/10] lg:aspect-auto overflow-hidden bg-slate-100">
                <img
                  src={featured.cover}
                  alt={featured.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold">
                  {featured.category}
                </div>
                <h2 className="mt-3 font-heading text-3xl lg:text-4xl font-light tracking-tight text-slate-900 group-hover:text-teal-700 transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
                  <span className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" /> {featured.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5" /> {featured.readMinutes} min read
                  </span>
                  <span>{featured.date}</span>
                </div>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-teal-700 group-hover:gap-2 transition-all">
                  Read article <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Grid */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-6">
            {rest.map((b, i) => (
              <Link
                key={b.slug}
                to={`/blog/${b.slug}`}
                className="reveal group bg-white border border-slate-100 rounded-3xl overflow-hidden premium-card"
                data-testid={`blog-card-${b.slug}`}
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                  <img
                    src={b.cover}
                    alt={b.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-7">
                  <div className="text-xs uppercase tracking-[0.22em] text-teal-600 font-semibold">
                    {b.category}
                  </div>
                  <h3 className="mt-2 font-heading text-xl font-medium text-slate-900 group-hover:text-teal-700 transition-colors">
                    {b.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 line-clamp-2">{b.excerpt}</p>
                  <div className="mt-5 flex items-center gap-3 text-xs text-slate-500">
                    <span>{b.date}</span>
                    <span>•</span>
                    <span>{b.readMinutes} min read</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
